import React, { useState, useRef, useEffect } from 'react';
import Toast from './Toast';

interface StyleAnalyzerModalProps {
  isOpen: boolean;
  onClose: () => void;
}

type AnalysisStep = 'upload' | 'analyzing' | 'results';

interface SkinAnalysisData {
  rgb: number[];
  hex: string;
  dominant_colors: Array<{
    rgb: number[];
    hex: string;
    percentage: number;
  }>;
  category: string;
  description: string;
  ita_angle: number;
  undertone: string;
  brightness: string;
  saturation: string;
  confidence: number;
}

interface RecommendedColor {
  color_name: string;
  color_hex: string;
  skin_match: string;
  undertone_type: string;
  color_type: string;
  vibe: string;
  notes: string;
}

interface Product {
  id: string;
  productTitle: string;
  price?: number;
  currency?: string;
  imagePath?: string;
  productLink?: string;
  dominantColor?: string;
  dominantColorName?: string;
  fabric?: string;
  season?: string;
  formalityLevel?: string;
  occasionFit?: string;
  store?: {
    name: string;
    websiteUrl?: string;
  };
}

interface ChromaNamedField {
  id: number;
  name: string;
  notes?: string;
}

interface ChromaStore {
  id: string;
  name: string;
  websiteUrl?: string;
}

interface ChromaProductResult {
  id: string;
  productTitle?: string;
  price?: number | string;
  currency?: string;
  imagePath?: string;
  productLink?: string;
  dominantColor?: string;
  dominantColorName?: string;
  fabric?: ChromaNamedField | string;
  season?: ChromaNamedField | string;
  formalityLevel?: ChromaNamedField | string;
  occasionFit?: ChromaNamedField | string;
  store?: ChromaStore;
}

interface RecommendationsData {
  skin_tone: string;
  total_recommended_colors: number;
  recommended_colors: RecommendedColor[];
  products: {
    Male: Product[];
    Female: Product[];
  };
}

const API_BASE = 'https://api.thedressup.co';
const PRODUCT_IMAGE_PLACEHOLDER = 'https://via.placeholder.com/300x400?text=Product+Image';
const S3_BASE = 'https://dressup-storage.s3.ap-south-1.amazonaws.com';

const getProductImageUrl = (imagePath?: string): string => {
  if (!imagePath) return PRODUCT_IMAGE_PLACEHOLDER;
  if (imagePath.startsWith('http://') || imagePath.startsWith('https://')) {
    return imagePath;
  }

  const normalizedPath = imagePath.replace('/images', '');
  return `${S3_BASE}${normalizedPath.startsWith('/') ? normalizedPath : `/${normalizedPath}`}`;
};

const parseProductPrice = (price?: number | string): number | undefined => {
  if (price == null || price === '') return undefined;
  const numericPrice = typeof price === 'number' ? price : Number(price);
  return Number.isFinite(numericPrice) ? numericPrice : undefined;
};

const formatProductPrice = (price?: number, currency?: string): string | null => {
  if (price == null) return null;
  const formattedPrice = price.toFixed(2);
  return currency ? `${currency} ${formattedPrice}` : formattedPrice;
};

const getNamedField = (value?: ChromaNamedField | string): string | undefined => {
  if (!value) return undefined;
  return typeof value === 'string' ? value : value.name;
};

const mapChromaProduct = (item: ChromaProductResult): Product => ({
  id: item.id,
  productTitle: item.productTitle ?? 'Untitled product',
  price: parseProductPrice(item.price),
  currency: item.currency,
  imagePath: item.imagePath,
  productLink: item.productLink,
  dominantColor: item.dominantColor,
  dominantColorName: item.dominantColorName,
  fabric: getNamedField(item.fabric),
  season: getNamedField(item.season),
  formalityLevel: getNamedField(item.formalityLevel),
  occasionFit: getNamedField(item.occasionFit),
  store: item.store
    ? { name: item.store.name, websiteUrl: item.store.websiteUrl }
    : undefined,
});

const fetchRecommendedColors = async (skinTone: string) => {
  const response = await fetch(
    `${API_BASE}/products/recommended-colors?skin_tone=${encodeURIComponent(skinTone)}`,
    {
      method: 'GET',
      headers: { accept: 'application/json' },
    }
  );

  if (!response.ok) {
    const errorData = await response.json().catch(() => null);
    const errorMessage = errorData?.detail || errorData?.message || 'Failed to get color recommendations';
    throw new Error(errorMessage);
  }

  return response.json() as Promise<{
    skin_tone: string;
    total_recommended_colors: number;
    recommended_colors: RecommendedColor[];
  }>;
};

const fetchChromaProducts = async (skinTone: string, gender: 'Male' | 'Female') => {
  const response = await fetch(`${API_BASE}/chroma/query/public`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      accept: 'application/json',
    },
    body: JSON.stringify({
      skin_tone: skinTone.toLowerCase(),
      gender,
      limit: 10,
    }),
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => null);
    const errorMessage = errorData?.detail || errorData?.message || 'Failed to get product recommendations';
    throw new Error(errorMessage);
  }

  const data = await response.json() as { results: ChromaProductResult[] };
  return (data.results ?? []).map(mapChromaProduct);
};

interface ProductRecommendationCardProps {
  product: Product;
}

const ProductRecommendationCard: React.FC<ProductRecommendationCardProps> = ({ product }) => {
  const formattedPrice = formatProductPrice(product.price, product.currency);
  const Wrapper = product.productLink ? 'a' : 'div';
  const wrapperProps = product.productLink
    ? {
        href: product.productLink,
        target: '_blank' as const,
        rel: 'noopener noreferrer',
      }
    : {};

  const attributeTags = [
    product.fabric && { label: product.fabric, icon: 'fabric' as const },
    product.formalityLevel && { label: product.formalityLevel, icon: 'formality' as const },
  ].filter(Boolean) as Array<{ label: string; icon: 'fabric' | 'formality' }>;

  return (
    <Wrapper
      {...wrapperProps}
      className="group flex flex-col sm:flex-row rounded-none border border-white/10 bg-gradient-to-br from-white/[0.06] to-white/[0.02] hover:border-[#7f92ff]/40 hover:from-white/[0.08] hover:to-white/[0.03] transition-all duration-300 overflow-hidden"
    >
      {/* Image */}
      <div className="relative sm:w-[42%] shrink-0">
        <div className="aspect-[4/5] sm:aspect-auto sm:h-full min-h-[220px] bg-white/95 flex items-center justify-center p-5 sm:p-6">
          <img
            src={getProductImageUrl(product.imagePath)}
            alt={product.productTitle}
            className="max-w-full max-h-full w-auto h-auto object-contain transition-transform duration-500 group-hover:scale-[1.03]"
            loading="lazy"
            onError={(e) => {
              (e.target as HTMLImageElement).src = PRODUCT_IMAGE_PLACEHOLDER;
            }}
          />
        </div>
        {product.dominantColor && (
          <div
            className="absolute bottom-3 left-3 flex items-center gap-2 pl-1.5 pr-3 py-1.5 rounded-none bg-panel/85 backdrop-blur-sm border border-white/15 shadow-lg"
            title={product.dominantColorName}
          >
            <span
              className="w-5 h-5 rounded-full border-2 border-white/40 shrink-0"
              style={{ backgroundColor: product.dominantColor }}
            />
            {product.dominantColorName && (
              <span className="text-[11px] font-medium text-white capitalize leading-none">
                {product.dominantColorName}
              </span>
            )}
          </div>
        )}
      </div>

      {/* Details */}
      <div className="flex flex-1 flex-col p-4 sm:p-5 min-w-0">
        {/* Header: store + season */}
        <div className="flex items-start justify-between gap-2 mb-2.5">
          {product.store?.name ? (
            <span className="inline-flex items-center gap-1.5 text-[10px] font-bold tracking-[0.14em] uppercase text-[#7f92ff]">
              <svg className="w-3 h-3 shrink-0 opacity-80" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
              </svg>
              {product.store.name}
            </span>
          ) : (
            <span />
          )}
          {product.season && (
            <span className="shrink-0 px-2 py-0.5 rounded-none bg-[#7f92ff]/15 border border-[#7f92ff]/30 text-[10px] font-semibold uppercase tracking-wide text-[#7f92ff]">
              {product.season}
            </span>
          )}
        </div>

        {/* Title */}
        <h5 className="font-medium text-white text-[15px] leading-snug mb-3 line-clamp-2 group-hover:text-white/95">
          {product.productTitle}
        </h5>

        {/* Attribute tags */}
        {attributeTags.length > 0 && (
          <div className="flex flex-wrap gap-1.5 mb-3">
            {attributeTags.map(({ label, icon }) => (
              <span
                key={`${icon}-${label}`}
                className="inline-flex items-center gap-1 px-2.5 py-1 rounded-none bg-white/5 border border-white/10 text-[11px] text-white/65"
              >
                {icon === 'fabric' && (
                  <svg className="w-3 h-3 shrink-0 text-white/40" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 5a1 1 0 011-1h14a1 1 0 011 1v2a1 1 0 01-1 1H5a1 1 0 01-1-1V5zM4 13a1 1 0 011-1h6a1 1 0 011 1v6a1 1 0 01-1 1H5a1 1 0 01-1-1v-6zM16 13a1 1 0 011-1h2a1 1 0 011 1v6a1 1 0 01-1 1h-2a1 1 0 01-1-1v-6z" />
                  </svg>
                )}
                {icon === 'formality' && (
                  <svg className="w-3 h-3 shrink-0 text-white/40" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z" />
                  </svg>
                )}
                {label}
              </span>
            ))}
          </div>
        )}

        {/* Occasion */}
        {product.occasionFit && (
          <div className="mb-4 p-2.5 rounded-none bg-white/[0.03] border border-white/8">
            <p className="text-[10px] font-semibold uppercase tracking-[0.12em] text-white/40 mb-1">Best for</p>
            <p className="text-xs text-white/60 leading-relaxed line-clamp-2">{product.occasionFit}</p>
          </div>
        )}

        {/* Footer */}
        <div className="flex items-center justify-between gap-3 mt-auto pt-3 border-t border-white/10">
          {formattedPrice ? (
            <span className="text-lg font-semibold text-white tracking-tight">{formattedPrice}</span>
          ) : (
            <span />
          )}
          {product.productLink && (
            <span className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-none bg-white/10 group-hover:bg-[#7f92ff] border border-white/15 group-hover:border-[#7f92ff] text-[10px] font-bold tracking-[0.08em] uppercase text-white group-hover:text-[#111113] transition-colors duration-200">
              Shop
              <svg className="w-3.5 h-3.5 group-hover:translate-x-0.5 transition-transform" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 5l7 7m0 0l-7 7m7-7H3" />
              </svg>
            </span>
          )}
        </div>
      </div>
    </Wrapper>
  );
};

const ANALYSIS_LOADING_STEPS = [
  'Detecting skin tone...',
  'Analyzing undertones...',
  'Matching color palette...',
  'Generating recommendations...',
] as const;

const StyleAnalyzerModal: React.FC<StyleAnalyzerModalProps> = ({ isOpen, onClose }) => {
  const [step, setStep] = useState<AnalysisStep>('upload');
  const [selectedImage, setSelectedImage] = useState<string | null>(null);
  const [isCameraActive, setIsCameraActive] = useState(false);
  const [skinAnalysis, setSkinAnalysis] = useState<SkinAnalysisData | null>(null);
  const [recommendations, setRecommendations] = useState<RecommendationsData | null>(null);
  const [selectedGender, setSelectedGender] = useState<'Male' | 'Female'>('Female');
  const [error, setError] = useState<string | null>(null);
  const [toast, setToast] = useState<{ message: string; type: 'error' | 'success' | 'info' } | null>(null);
  const [isCameraInitializing, setIsCameraInitializing] = useState(false);
  const [isCameraManuallyClosed, setIsCameraManuallyClosed] = useState(false);
  const [availableCameras, setAvailableCameras] = useState<MediaDeviceInfo[]>([]);
  const [currentCameraId, setCurrentCameraId] = useState<string | null>(null);
  const [loadingStep, setLoadingStep] = useState(0);
  const videoRef = useRef<HTMLVideoElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const showToast = (message: string, type: 'error' | 'success' | 'info' = 'error') => {
    setToast({ message, type });
    console.log(`[Toast ${type.toUpperCase()}]`, message);
  };

  const enumerateCameras = async (): Promise<MediaDeviceInfo[]> => {
    try {
      if (!navigator.mediaDevices?.enumerateDevices) {
        return [];
      }
      const devices = await navigator.mediaDevices.enumerateDevices();
      const videoDevices = devices.filter(d => d.kind === 'videoinput');
      setAvailableCameras(videoDevices);
      return videoDevices;
    } catch (err) {
      console.error('[Camera] Error enumerating cameras:', err);
      return [];
    }
  };

  const logCameraInfo = () => {
    console.log('=== Camera Initialization Debug Info ===');
    console.log('User Agent:', navigator.userAgent);
    console.log('Platform:', navigator.platform);
    console.log('MediaDevices available:', !!navigator.mediaDevices);
    console.log('getUserMedia available:', !!navigator.mediaDevices?.getUserMedia);
    console.log('EnumerateDevices available:', !!navigator.mediaDevices?.enumerateDevices);
    
    if (navigator.mediaDevices?.enumerateDevices) {
      navigator.mediaDevices.enumerateDevices()
        .then(devices => {
          console.log('Available devices:', devices.length);
          const videoDevices = devices.filter(d => d.kind === 'videoinput');
          console.log('Video input devices:', videoDevices.length);
          videoDevices.forEach((device, index) => {
            console.log(`  Device ${index + 1}:`, {
              label: device.label || 'No label (permission required)',
              deviceId: device.deviceId,
              groupId: device.groupId
            });
          });
        })
        .catch(err => console.error('Error enumerating devices:', err));
    }
    
    // Check permissions
    if (navigator.permissions) {
      navigator.permissions.query({ name: 'camera' as PermissionName })
        .then(result => {
          console.log('Camera permission status:', result.state);
        })
        .catch(err => console.warn('Permission query not supported:', err));
    }
    console.log('========================================');
  };

  const startCamera = React.useCallback(async (deviceId?: string) => {
    console.log('[Camera] Starting camera initialization...', deviceId ? `with device: ${deviceId}` : '');
    setIsCameraInitializing(true);
    setIsCameraManuallyClosed(false);
    logCameraInfo();
    
    // Enumerate cameras first to get available devices
    await enumerateCameras();

    // Check if getUserMedia is available
    if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
      const errorMsg = 'Camera API not supported in this browser. Please use a modern browser or upload a photo instead.';
      console.error('[Camera] getUserMedia not available');
      showToast(errorMsg, 'error');
      setError(errorMsg);
      setIsCameraActive(false);
      return;
    }

    // Try multiple camera constraint configurations (fallback strategy)
    const cameraConfigs = deviceId 
      ? [
          // If specific device requested, try it first
          {
            name: `Specific device (${deviceId})`,
            constraints: { 
              video: { 
                deviceId: { exact: deviceId },
                width: { ideal: 1280 }, 
                height: { ideal: 720 } 
              } 
            }
          },
          {
            name: `Specific device (${deviceId}) - any resolution`,
            constraints: { 
              video: { deviceId: { exact: deviceId } } 
            }
          }
        ]
      : [
          {
            name: 'High quality (1280x720)',
            constraints: { 
              video: { 
                facingMode: 'user', 
                width: { ideal: 1280 }, 
                height: { ideal: 720 } 
              } 
            }
          },
          {
            name: 'Medium quality (640x480)',
            constraints: { 
              video: { 
                facingMode: 'user', 
                width: { ideal: 640 }, 
                height: { ideal: 480 } 
              } 
            }
          },
          {
            name: 'Any resolution (facingMode: user)',
            constraints: { 
              video: { facingMode: 'user' } 
            }
          },
          {
            name: 'Any resolution (facingMode: environment)',
            constraints: { 
              video: { facingMode: 'environment' } 
            }
          },
          {
            name: 'Any available camera',
            constraints: { 
              video: true 
            }
          }
        ];

    let lastError: Error | null = null;

    for (let i = 0; i < cameraConfigs.length; i++) {
      const config = cameraConfigs[i];
      console.log(`[Camera] Attempt ${i + 1}/${cameraConfigs.length}: Trying ${config.name}`, config.constraints);
      
      try {
        const stream = await navigator.mediaDevices.getUserMedia(config.constraints);
        
        // Log successful stream info
        const videoTrack = stream.getVideoTracks()[0];
        if (videoTrack) {
          const settings = videoTrack.getSettings();
          const deviceId = settings.deviceId;
          setCurrentCameraId(deviceId || null);
          console.log('[Camera] Success! Stream obtained:', {
            config: config.name,
            width: settings.width,
            height: settings.height,
            facingMode: settings.facingMode,
            deviceId: deviceId,
            frameRate: settings.frameRate
          });
        }

        if (videoRef.current) {
          const video = videoRef.current;
          video.srcObject = stream;
          streamRef.current = stream;
          
          // Wait for video to be ready
          await new Promise<void>((resolve, reject) => {
            // Check if video is already ready
            if (video.readyState >= HTMLMediaElement.HAVE_METADATA) {
              console.log('[Camera] Video already ready:', {
                videoWidth: video.videoWidth,
                videoHeight: video.videoHeight,
                readyState: video.readyState
              });
              resolve();
              return;
            }
            
            const onLoadedMetadata = () => {
              console.log('[Camera] Video metadata loaded:', {
                videoWidth: video.videoWidth,
                videoHeight: video.videoHeight,
                readyState: video.readyState
              });
              cleanup();
              resolve();
            };
            
            const onError = (e: Event) => {
              console.error('[Camera] Video element error:', e);
              const errorEvent = e as ErrorEvent;
              console.error('[Camera] Error details:', {
                message: errorEvent.message,
                filename: errorEvent.filename,
                lineno: errorEvent.lineno,
                colno: errorEvent.colno,
                error: errorEvent.error
              });
              cleanup();
              reject(new Error(`Video element failed to load: ${errorEvent.message || 'Unknown error'}`));
            };
            
            const cleanup = () => {
              video.removeEventListener('loadedmetadata', onLoadedMetadata);
              video.removeEventListener('error', onError);
              if (timeoutId) clearTimeout(timeoutId);
            };
            
            let timeoutId: ReturnType<typeof setTimeout> | null = null;
            
            video.addEventListener('loadedmetadata', onLoadedMetadata);
            video.addEventListener('error', onError);
            
            // Timeout after 10 seconds (increased for slower devices)
            timeoutId = setTimeout(() => {
              console.warn('[Camera] Video loading timeout after 10 seconds');
              cleanup();
              reject(new Error('Video loading timeout - camera may be slow or unavailable'));
            }, 10000);
          });

          setIsCameraActive(true);
          setIsCameraInitializing(false);
          setError(null);
          console.log('[Camera] Camera activated successfully');
          showToast('Camera initialized successfully', 'success');
          return; // Success, exit the function
        } else {
          stream.getTracks().forEach(track => track.stop());
          throw new Error('Video element reference not available');
        }
      } catch (err) {
        const error = err as Error;
        lastError = error;
        console.error(`[Camera] Attempt ${i + 1} failed:`, {
          name: config.name,
          error: error.name,
          message: error.message,
          stack: error.stack
        });

        // If this is the last attempt, we'll handle the error below
        if (i < cameraConfigs.length - 1) {
          console.log(`[Camera] Retrying with next configuration...`);
          continue;
        }
      }
    }

    // All attempts failed
    console.error('[Camera] All camera initialization attempts failed');
    setIsCameraActive(false);
    setIsCameraInitializing(false);
    
    let errorMessage = 'Unable to access camera. ';
    if (lastError) {
      console.error('[Camera] Last error details:', {
        name: lastError.name,
        message: lastError.message
      });

      if (lastError.name === 'NotAllowedError' || lastError.name === 'PermissionDeniedError') {
        errorMessage += 'Camera permission was denied. Please allow camera access in your browser settings and try again.';
      } else if (lastError.name === 'NotFoundError' || lastError.name === 'DevicesNotFoundError') {
        errorMessage += 'No camera found on this device. Please upload a photo instead.';
      } else if (lastError.name === 'NotReadableError' || lastError.name === 'TrackStartError') {
        errorMessage += 'Camera is being used by another application. Please close other apps using the camera and try again.';
      } else if (lastError.name === 'OverconstrainedError' || lastError.name === 'ConstraintNotSatisfiedError') {
        errorMessage += 'Camera does not support the required settings. Please upload a photo instead.';
      } else if (lastError.name === 'SecurityError') {
        errorMessage += 'Camera access blocked for security reasons. Please use HTTPS or upload a photo instead.';
      } else {
        errorMessage += `Error: ${lastError.message || 'Unknown error'}. Please upload a photo instead.`;
      }
    } else {
      errorMessage += 'Please upload a photo instead.';
    }

    setError(errorMessage);
    showToast(errorMessage, 'error');
  }, []);

  const stopCamera = React.useCallback(() => {
    console.log('[Camera] Stopping camera...');
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => {
        console.log('[Camera] Stopping track:', track.kind, track.label);
        track.stop();
      });
      streamRef.current = null;
    }
    if (videoRef.current) {
      videoRef.current.srcObject = null;
    }
    setIsCameraActive(false);
    console.log('[Camera] Camera stopped');
  }, []);

  const switchCamera = React.useCallback(async () => {
    console.log('[Camera] Switching camera...');
    const cameras = await enumerateCameras();
    
    if (cameras.length < 2) {
      showToast('Only one camera available on this device', 'info');
      return;
    }

    // Find current camera index
    const currentIndex = currentCameraId 
      ? cameras.findIndex(c => c.deviceId === currentCameraId)
      : -1;
    
    // Get next camera (or first if at end)
    const nextIndex = (currentIndex + 1) % cameras.length;
    const nextCamera = cameras[nextIndex];
    
    console.log('[Camera] Switching to camera:', {
      index: nextIndex,
      deviceId: nextCamera.deviceId,
      label: nextCamera.label
    });

    // Stop current stream
    stopCamera();
    
    // Small delay to ensure stream is fully stopped
    await new Promise(resolve => setTimeout(resolve, 300));
    
    // Start with new camera
    await startCamera(nextCamera.deviceId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentCameraId, stopCamera, startCamera]);

  const handleCloseCamera = React.useCallback(() => {
    console.log('[Camera] Camera closed manually by user');
    stopCamera();
    setIsCameraManuallyClosed(true);
  }, [stopCamera]);

  useEffect(() => {
    if (isOpen) {
      console.log('[Modal] Modal opened, starting camera...');
      setIsCameraManuallyClosed(false);
      setIsCameraInitializing(true);
      // Start camera automatically when modal opens
      startCamera();
    } else {
      console.log('[Modal] Modal closed, stopping camera...');
      stopCamera();
      setStep('upload');
      setSelectedImage(null);
      setIsCameraActive(false);
      setIsCameraInitializing(false);
      setIsCameraManuallyClosed(false);
      setError(null);
      setToast(null);
      setCurrentCameraId(null);
    }
  }, [isOpen, startCamera, stopCamera]);

  useEffect(() => {
    if (step !== 'analyzing') {
      setLoadingStep(0);
      return;
    }

    setLoadingStep(0);
    const interval = setInterval(() => {
      setLoadingStep((prev) =>
        prev < ANALYSIS_LOADING_STEPS.length - 1 ? prev + 1 : prev
      );
    }, 1400);

    return () => clearInterval(interval);
  }, [step]);

  const capturePhoto = () => {
    if (videoRef.current && canvasRef.current) {
      const video = videoRef.current;
      const canvas = canvasRef.current;
      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;
      const ctx = canvas.getContext('2d');
      if (ctx) {
        ctx.drawImage(video, 0, 0);
        const imageData = canvas.toDataURL('image/jpeg');
        setSelectedImage(imageData);
        stopCamera();
        
        // Convert canvas to blob for API upload
        canvas.toBlob((blob) => {
          if (blob) {
            analyzeImage(blob);
          }
        }, 'image/jpeg', 0.95);
      }
    }
  };

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      // Validate file type
      const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];
      if (!allowedTypes.includes(file.type.toLowerCase())) {
        setError('Invalid file type. Please upload a JPG, PNG, or WebP image.');
        event.target.value = ''; // Reset file input
        return;
      }

      // Validate file size (max 10MB)
      const maxSize = 10 * 1024 * 1024; // 10MB
      if (file.size > maxSize) {
        setError('File is too large. Please upload an image smaller than 10MB.');
        event.target.value = ''; // Reset file input
        return;
      }

      setError(null);
      const reader = new FileReader();
      reader.onload = (e) => {
        setSelectedImage(e.target?.result as string);
        analyzeImage(file);
      };
      reader.readAsDataURL(file);
    }
  };

  const analyzeImage = async (imageFile: File | Blob) => {
    setStep('analyzing');
    setError(null);

    try {
      // Step 1: Analyze skin tone
      const formData = new FormData();
      
      // If it's a Blob (from camera), we need to create a File object with proper name and type
      if (imageFile instanceof Blob && !(imageFile instanceof File)) {
        const file = new File([imageFile], 'photo.jpg', { type: 'image/jpeg' });
        formData.append('file', file);
      } else {
        // It's already a File object from file input
        formData.append('file', imageFile);
      }

      const skinAnalysisResponse = await fetch(
        'https://api.thedressup.co/skin-detection/analyze?mask_shrink_factor=0.35',
        {
          method: 'POST',
          body: formData,
        }
      );

      if (!skinAnalysisResponse.ok) {
        const errorData = await skinAnalysisResponse.json().catch(() => null);
        const errorMessage = errorData?.detail || errorData?.message || 'Failed to analyze skin tone';
        throw new Error(errorMessage);
      }

      const skinData = await skinAnalysisResponse.json();
      
      if (!skinData.success || !skinData.data) {
        throw new Error('Invalid response from skin analysis API');
      }

      setSkinAnalysis(skinData.data);

      const skinTone = skinData.data.undertone;

      const [colorsData, femaleProducts, maleProducts] = await Promise.all([
        fetchRecommendedColors(skinTone),
        fetchChromaProducts(skinTone, 'Female'),
        fetchChromaProducts(skinTone, 'Male'),
      ]);

      setRecommendations({
        skin_tone: colorsData.skin_tone,
        total_recommended_colors: colorsData.total_recommended_colors,
        recommended_colors: colorsData.recommended_colors,
        products: {
          Female: femaleProducts,
          Male: maleProducts,
        },
      });

      setStep('results');
    } catch (err) {
      console.error('Error analyzing image:', err);
      setError(err instanceof Error ? err.message : 'Failed to analyze image. Please try again.');
      setStep('upload');
      setSelectedImage(null); // Clear the image on error so user can try again
    }
  };

  const resetAnalysis = () => {
    setStep('upload');
    setSelectedImage(null);
    setIsCameraActive(false);
    setSkinAnalysis(null);
    setRecommendations(null);
    setError(null);
    stopCamera();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 animate-fade-in">
      {/* Toast Notification */}
      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          isVisible={!!toast}
          onClose={() => setToast(null)}
          duration={toast.type === 'error' ? 8000 : 5000}
        />
      )}

      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-black/70 backdrop-blur-sm"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="relative w-full max-w-4xl max-h-[90vh] flex flex-col bg-panel border border-white/10 rounded-none font-sans shadow-2xl animate-bounce-in overflow-hidden">
        {/* Close Button */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 z-20 p-2 rounded-none bg-white/10 hover:bg-white/20 border border-white/10 transition-colors duration-200 group"
        >
          <svg className="w-6 h-6 text-white/70 group-hover:text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>

        {/* Scrollable content: single scroll container so scrollbar stays on the modal edge */}
        <div className="flex-1 min-h-0 overflow-y-auto overscroll-contain [scrollbar-gutter:stable]">
          <div className="p-6 md:p-10">
          {/* Upload Step */}
          {step === 'upload' && (
            <div className="animate-fade-in">
              <div className="text-center mb-6">
                <span className="block text-[#7f92ff] text-[10px] font-semibold tracking-[0.22em] uppercase mb-4">
                  Color Analysis
                </span>
                <h2 className="font-sans font-light text-white text-3xl md:text-4xl leading-tight tracking-[-0.025em] mb-3">
                  {isCameraActive ? (
                    <>Smile for the <em className="font-semibold italic">camera</em></>
                  ) : (
                    <>Upload your <em className="font-semibold italic">photo</em></>
                  )}
                </h2>
                <p className="text-gray-300 text-sm md:text-base">
                  {isCameraActive ? 'Position yourself in the frame and click capture' : 'Starting camera...'}
                </p>
              </div>

              {/* Camera View Container - Always rendered */}
              <div className="mb-6">
                <div className="relative overflow-hidden rounded-none border border-white/10 bg-black min-h-[400px]">
                  {/* Video Element - Always present */}
                  <video
                    ref={videoRef}
                    autoPlay
                    playsInline
                    className={`w-full h-auto min-h-[400px] object-cover transition-opacity duration-500 -scale-x-100 ${
                      isCameraActive ? 'opacity-100' : 'opacity-0'
                    }`}
                  />
                  <canvas ref={canvasRef} className="hidden" />
                  
                  {/* Loading Overlay - Shows while camera initializes */}
                  {isCameraInitializing && !isCameraActive && (
                    <div className="absolute inset-0 flex items-center justify-center bg-black/90 backdrop-blur-sm animate-fade-in">
                      <div className="text-center">
                        <div className="inline-block relative mb-4">
                          <div className="animate-spin rounded-full h-16 w-16 border-4 border-white/20 border-t-white"></div>
                        </div>
                        <p className="text-white mb-6 text-lg">Initializing camera...</p>
                        
                        {/* Alternative Upload Option */}
                        <button
                          onClick={() => fileInputRef.current?.click()}
                          className="inline-flex items-center gap-2 px-6 py-3 rounded-none bg-white hover:bg-gray-100 text-panel text-xs font-bold tracking-[0.06em] uppercase transition-colors duration-150"
                        >
                          <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                          </svg>
                          Or Upload from Gallery
                        </button>
                      </div>
                    </div>
                  )}

                  {/* Camera Closed State - Show button to restart camera */}
                  {!isCameraActive && !isCameraInitializing && isCameraManuallyClosed && (
                    <div className="absolute inset-0 flex items-center justify-center bg-black/90 backdrop-blur-sm animate-fade-in">
                      <div className="text-center">
                        <svg className="w-12 h-12 text-white/40 mx-auto mb-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" />
                        </svg>
                        <p className="text-white mb-6 text-lg font-light">Camera Closed</p>
                        
                        <div className="flex flex-col gap-4 items-center">
                          <button
                            onClick={() => startCamera()}
                            className="inline-flex items-center gap-2 px-8 py-4 rounded-none bg-white hover:bg-gray-100 text-panel text-xs font-bold tracking-[0.06em] uppercase transition-colors duration-150"
                          >
                            <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
                            </svg>
                            Start Camera
                          </button>
                          
                          <button
                            onClick={() => fileInputRef.current?.click()}
                            className="inline-flex items-center gap-2 px-6 py-3 rounded-none border border-white/20 text-white text-xs font-bold tracking-[0.06em] uppercase hover:bg-white/10 transition-colors duration-150"
                          >
                            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                            </svg>
                            Upload from Gallery
                          </button>
                        </div>
                      </div>
                    </div>
                  )}
                  
                  {/* Camera Frame Overlay - Only when camera is active */}
                  {isCameraActive && (
                    <div className="absolute inset-0 pointer-events-none animate-fade-in">
                      <div className="absolute inset-8 border-2 border-white/30 rounded-full" />
                    </div>
                  )}
                  
                  {/* Camera Controls - Only when camera is active */}
                  {isCameraActive && (
                    <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent p-8 animate-fade-in">
                      <div className="flex items-center justify-center gap-4 md:gap-6">
                        {/* Upload Alternative */}
                        <button
                          onClick={() => fileInputRef.current?.click()}
                          className="p-4 rounded-none bg-white/10 border border-white/20 hover:bg-white/20 transition-colors duration-200 group"
                          title="Upload from gallery"
                        >
                          <svg className="w-6 h-6 text-white/70 group-hover:text-white transition-colors" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                          </svg>
                        </button>

                        {/* Switch Camera Button - Only show if multiple cameras available */}
                        {availableCameras.length > 1 && (
                          <button
                            onClick={switchCamera}
                            className="p-4 rounded-none bg-white/10 border border-white/20 hover:bg-white/20 transition-colors duration-200 group"
                            title="Switch camera"
                          >
                            <svg className="w-6 h-6 text-white/70 group-hover:text-white transition-colors" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                            </svg>
                          </button>
                        )}

                        {/* Capture Button */}
                        <button
                          onClick={capturePhoto}
                          className="relative group p-1 rounded-full bg-white hover:bg-gray-100 transition-colors duration-200"
                        >
                          <div className="w-16 h-16 rounded-full bg-panel flex items-center justify-center">
                            <svg className="w-8 h-8 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
                            </svg>
                          </div>
                        </button>

                        {/* Close Camera */}
                        <button
                          onClick={handleCloseCamera}
                          className="p-4 rounded-none bg-white/10 border border-white/20 hover:bg-white/20 transition-colors duration-200 group"
                          title="Close camera"
                        >
                          <svg className="w-6 h-6 text-white/70 group-hover:text-white transition-colors" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                          </svg>
                        </button>
                      </div>
                      
                      <p className="text-white text-center mt-4 text-sm font-medium">
                        Click the center button to capture
                      </p>
                    </div>
                  )}
                </div>
              </div>

              <input
                ref={fileInputRef}
                type="file"
                accept="image/jpeg,image/jpg,image/png,image/webp"
                onChange={handleFileSelect}
                className="hidden"
              />

              {/* Error Message */}
              {error && (
                <div className="mb-6 p-4 rounded-none border border-red-500/30 bg-red-500/10 animate-fade-in">
                  <div className="flex items-start gap-3">
                    <svg className="w-5 h-5 text-red-400 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                    </svg>
                    <div>
                      <h4 className="font-semibold text-red-300 mb-1">Analysis Failed</h4>
                      <p className="text-sm text-red-200/80">{error}</p>
                    </div>
                  </div>
                </div>
              )}

              {/* Tips */}
              <div className="rounded-none border border-white/10 bg-white/5 p-6">
                <h4 className="font-semibold text-white mb-3 flex items-center gap-2 text-sm tracking-wide uppercase">
                  <svg className="w-5 h-5 text-[#7f92ff]" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                  </svg>
                  Tips for best results
                </h4>
                <ul className="space-y-2 text-sm text-gray-300">
                  <li className="flex items-start gap-2">
                    <svg className="w-5 h-5 text-[#7f92ff] mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                    </svg>
                    Use natural lighting for accurate skin tone analysis
                  </li>
                  <li className="flex items-start gap-2">
                    <svg className="w-5 h-5 text-[#7f92ff] mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                    </svg>
                    Face the camera directly with minimal makeup
                  </li>
                  <li className="flex items-start gap-2">
                    <svg className="w-5 h-5 text-[#7f92ff] mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                    </svg>
                    Ensure your full face is visible in the frame
                  </li>
                </ul>
              </div>
            </div>
          )}

          {/* Analyzing Step */}
          {step === 'analyzing' && (
            <div className="py-12 md:py-16 text-center animate-fade-in">
              {selectedImage && (
                <div className="mb-10 flex justify-center">
                  <div className="relative w-40 h-40 md:w-48 md:h-48">
                    <img
                      src={selectedImage}
                      alt="Your photo"
                      className="absolute inset-3 w-[calc(100%-24px)] h-[calc(100%-24px)] object-cover rounded-full border border-white/20 z-10"
                    />
                    <div className="absolute inset-0 rounded-full border-2 border-white/10" />
                    <div className="absolute inset-0 rounded-full border-2 border-transparent border-t-white animate-spin" />
                    <div
                      className="absolute inset-1 rounded-full border-2 border-transparent border-b-[#7f92ff] animate-spin"
                      style={{ animationDirection: 'reverse', animationDuration: '1.4s' }}
                    />
                  </div>
                </div>
              )}

              <h3 className="font-sans font-light text-2xl md:text-3xl text-white mb-2">
                Analyzing your <em className="font-semibold italic">palette</em>
              </h3>
              <p className="text-gray-300 text-sm mb-10">This usually takes a few seconds</p>

              <div className="max-w-md mx-auto space-y-4">
                {ANALYSIS_LOADING_STEPS.map((text, index) => {
                  const isDone = index < loadingStep;
                  const isActive = index === loadingStep;

                  return (
                    <div
                      key={text}
                      className={`flex items-center gap-4 transition-colors duration-300 ${
                        isDone || isActive ? 'text-white' : 'text-white/30'
                      }`}
                    >
                      <div className="flex-shrink-0 w-5 h-5 flex items-center justify-center">
                        {isDone ? (
                          <svg className="w-4 h-4 text-[#7f92ff]" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                          </svg>
                        ) : isActive ? (
                          <div className="w-4 h-4 rounded-full border-2 border-white/20 border-t-white animate-spin" />
                        ) : (
                          <div className="w-2 h-2 rounded-full bg-white/20" />
                        )}
                      </div>

                      <div className="flex-1 h-px bg-white/10 overflow-hidden relative">
                        {isDone && <div className="absolute inset-0 bg-white/50" />}
                        {isActive && (
                          <div className="absolute inset-y-0 left-0 w-1/2 bg-white/60 animate-analyze-slide" />
                        )}
                      </div>

                      <span className="text-[10px] tracking-[0.12em] uppercase whitespace-nowrap min-w-[140px] text-left">
                        {text}
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Results Step */}
          {step === 'results' && skinAnalysis && recommendations && (
            <div className="animate-fade-in">
              {/* Header */}
              <div className="text-center mb-6 pb-4 border-b border-white/10">
                {selectedImage && (
                  <div className="mb-4 flex justify-center">
                    <img 
                      src={selectedImage} 
                      alt="Your photo" 
                      className="w-24 h-24 md:w-32 md:h-32 object-cover rounded-full border-2 border-white/20"
                    />
                  </div>
                )}
                <span className="block text-[#7f92ff] text-[10px] font-semibold tracking-[0.22em] uppercase mb-3">
                  Your Results
                </span>
                <h2 className="font-sans font-light text-2xl md:text-3xl text-white mb-2">
                  Your personal <em className="font-semibold italic">color profile</em>
                </h2>
                <p className="text-gray-300 text-sm">
                  AI-powered skin tone analysis results
                </p>
              </div>

              {/* Skin Analysis Card */}
              <div className="mb-6 p-6 rounded-none border border-white/10 bg-white/5">
                <div className="grid md:grid-cols-2 gap-6 mb-4">
                  <div>
                    <p className="text-[10px] text-[#7f92ff] font-semibold tracking-[0.18em] uppercase mb-1">Skin Tone Category</p>
                    <h3 className="text-2xl font-light text-white">
                      {skinAnalysis.category}
                    </h3>
                    <p className="text-sm text-gray-300 mt-1">{skinAnalysis.description}</p>
                  </div>
                  <div>
                    <p className="text-[10px] text-[#7f92ff] font-semibold tracking-[0.18em] uppercase mb-1">Undertone</p>
                    <p className="text-3xl font-light text-white">{skinAnalysis.undertone}</p>
                    <div className="flex gap-2 mt-2">
                      <span className="px-3 py-1 rounded-none border border-white/20 text-xs font-semibold text-gray-300">
                        {skinAnalysis.brightness}
                      </span>
                      <span className="px-3 py-1 rounded-none border border-white/20 text-xs font-semibold text-gray-300">
                        {skinAnalysis.saturation}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Dominant Colors & Confidence */}
                <div className="grid md:grid-cols-2 gap-6 pt-4 border-t border-white/10">
                  <div>
                    <p className="text-[10px] text-[#7f92ff] font-semibold tracking-[0.18em] uppercase mb-3">Your Skin Tones</p>
                    <div className="flex gap-3">
                      {skinAnalysis.dominant_colors.map((color, idx) => (
                        <div key={idx} className="flex-1">
                          <div 
                            className="h-20 rounded-none border border-white/20"
                            style={{ backgroundColor: color.hex }}
                            title={`${color.hex} (${color.percentage}%)`}
                          />
                          <p className="text-sm text-center mt-2 font-semibold text-white">
                            {color.percentage}%
                          </p>
                        </div>
                      ))}
                    </div>
                  </div>
                  <div className="flex items-center justify-center">
                    <div className="relative inline-block">
                      <svg className="w-32 h-32 transform -rotate-90">
                        <circle
                          cx="64"
                          cy="64"
                          r="54"
                          stroke="rgba(255,255,255,0.1)"
                          strokeWidth="10"
                          fill="transparent"
                        />
                        <circle
                          cx="64"
                          cy="64"
                          r="54"
                          stroke="white"
                          strokeWidth="10"
                          fill="none"
                          strokeDasharray={`${2 * Math.PI * 54}`}
                          strokeDashoffset={`${2 * Math.PI * 54 * (1 - skinAnalysis.confidence / 100)}`}
                          strokeLinecap="round"
                          className="transition-all duration-1000 ease-out"
                        />
                      </svg>
                      
                      <div className="absolute inset-0 flex flex-col items-center justify-center px-2">
                        <p className="text-2xl font-light text-white leading-none">
                          {skinAnalysis.confidence}%
                        </p>
                        <p className="text-[10px] font-semibold text-white/45 uppercase tracking-wide mt-1">
                          Confidence
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Recommended Colors */}
              <div className="mb-6">
                <h3 className="text-lg font-light text-white mb-4 flex items-center gap-2">
                  <svg className="w-5 h-5 text-[#7f92ff]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21a4 4 0 01-4-4V5a2 2 0 012-2h4a2 2 0 012 2v12a4 4 0 01-4 4zm0 0h12a2 2 0 002-2v-4a2 2 0 00-2-2h-2.343M11 7.343l1.657-1.657a2 2 0 012.828 0l2.829 2.829a2 2 0 010 2.828l-8.486 8.485M7 17h.01" />
                  </svg>
                  Your perfect colors ({recommendations.total_recommended_colors})
                </h3>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                  {recommendations.recommended_colors.map((color, index) => (
                    <div
                      key={index}
                      className="group relative p-4 rounded-none border border-white/10 bg-white/5 hover:border-white/25 transition-colors duration-200"
                    >
                      <div 
                        className="w-full h-20 mb-3 relative overflow-hidden rounded-none border border-white/10"
                        style={{ backgroundColor: color.color_hex }}
                      >
                        <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
                      </div>
                      <div>
                        <p className="font-semibold text-white text-sm">{color.color_name}</p>
                        <p className="text-xs text-white/45 mb-2">{color.color_hex}</p>
                        <div className="flex gap-1 flex-wrap mb-2">
                          <span className="px-2 py-0.5 rounded-none border border-white/20 text-white/70 text-xs font-medium">
                            {color.color_type}
                          </span>
                        </div>
                        <p className="text-xs text-gray-400 italic">&ldquo;{color.vibe}&rdquo;</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Product Recommendations */}
              <div className="mb-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-light text-white flex items-center gap-2">
                    <svg className="w-5 h-5 text-[#7f92ff]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z" />
                    </svg>
                    Shop your colors
                  </h3>
                  {/* Gender Selector */}
                  <div className="flex gap-1 rounded-none border border-white/10 p-1">
                    <button
                      onClick={() => setSelectedGender('Female')}
                      className={`px-4 py-2 rounded-none text-xs font-bold tracking-[0.06em] uppercase transition-all ${
                        selectedGender === 'Female'
                          ? 'bg-white text-panel'
                          : 'text-white/60 hover:text-white'
                      }`}
                    >
                      Women
                    </button>
                    <button
                      onClick={() => setSelectedGender('Male')}
                      className={`px-4 py-2 rounded-none text-xs font-bold tracking-[0.06em] uppercase transition-all ${
                        selectedGender === 'Male'
                          ? 'bg-white text-panel'
                          : 'text-white/60 hover:text-white'
                      }`}
                    >
                      Men
                    </button>
                  </div>
                </div>

                {recommendations.products[selectedGender]?.length > 0 && (
                  <div className="grid grid-cols-1 gap-5">
                    {recommendations.products[selectedGender].map((product) => (
                      <ProductRecommendationCard key={product.id} product={product} />
                    ))}
                  </div>
                )}
              </div>

              {/* Action Buttons */}
              <div className="flex flex-col sm:flex-row gap-3 mb-6">
                <button
                  onClick={resetAnalysis}
                  className="flex-1 px-6 py-4 rounded-none border border-white/20 text-white text-xs font-bold tracking-[0.06em] uppercase hover:bg-white/10 transition-colors duration-150"
                >
                  Try Another Photo
                </button>
                <button
                  onClick={() => {
                    onClose();
                    document.getElementById('signup-section')?.scrollIntoView({ behavior: 'smooth' });
                  }}
                  className="flex-1 px-6 py-4 rounded-none bg-white hover:bg-gray-100 text-panel text-xs font-bold tracking-[0.06em] uppercase transition-colors duration-150"
                >
                  Sign Up
                </button>
              </div>
            </div>
          )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default StyleAnalyzerModal;

